import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PRINCIPALE, contesto, traduci, useTraduzioni } from '../../i18n';
import { leggiDocumento, salvaBlob } from '../../utils/documenti';
import { trattieniFocus } from '../features/GalleriaFoto';
import VisorePdf from './VisorePdf';
import './anteprima.css';

// Anteprima di un documento con il login (immagine o PDF), "Scarica" e azioni in più (verifica, togli…).
// L'apertura finisce nel registro degli accessi del backend. È una finestra modale: Tab e Maiusc+Tab restano
// dentro (come il visore delle foto) e alla chiusura il focus torna dove era.
// onNonDisponibile(messaggio): il server dice che il documento non c'è più o non è più apribile (4xx): la pagina
// chiude l'anteprima e si rilegge. Senza, il messaggio resta nell'anteprima.

export const SPARITO = traduci(PRINCIPALE, 'documenti.anteprima.sparito');
// `lingua` ultimo parametro, italiano di base: lo usa anche la pagina dello staff
export const messaggioApertura = (codice, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (codice === 404 || codice === 410) return t('documenti.anteprima.sparito');
  return codice === 403 ? t('documenti.anteprima.non_apribile') : null;
};

const Anteprima = ({ doc, onChiudi, azioni = null, piede = null, leggi = null, onNonDisponibile = null }) => {
  const { t, l } = useTraduzioni();
  const [stato, setStato] = useState({ url: null, blob: null, errore: null });
  const [pagina, setPagina] = useState(1);
  const [pagine, setPagine] = useState(null);
  const chiudi = useRef(null);
  const finestra = useRef(null);
  const sparito = useRef(onNonDisponibile);
  sparito.current = onNonDisponibile;

  useEffect(() => {
    const prima = document.activeElement;
    // gli avvisi aperti (in alto) coprivano intestazione e bottoni dell'anteprima al telefono: si chiudono
    toast.dismiss?.();
    return () => { if (prima && document.contains(prima)) prima.focus?.(); };
  }, []);

  useEffect(() => {
    let url = null;
    let vivo = true;
    setPagina(1);
    setPagine(null);
    (leggi ? leggi() : leggiDocumento(doc, 'apri'))
      .then((blob) => {
        if (!vivo) return;
        url = window.URL.createObjectURL(blob);
        setStato({ url, blob, errore: null });
      })
      .catch((e) => {
        if (!vivo) return;
        // Prima "Riprovate tra poco" anche per un documento tolto nel frattempo (404): riprovare non serve
        const messaggio = messaggioApertura(e?.response?.status, l);
        if (messaggio && sparito.current) { sparito.current(messaggio); return; }
        setStato({ url: null, blob: null, errore: messaggio ? `${messaggio}.` : t('documenti.anteprima.non_aperto') });
      });
    return () => { vivo = false; if (url) window.URL.revokeObjectURL(url); };
  }, [doc]); // eslint-disable-line

  // Il fuoco va su "Chiudi" una volta sola, all'apertura. Prima l'effetto dipendeva da onChiudi (una funzione nuova a
  // ogni render della pagina): dopo "Togli" e a ogni rilettura della pagina il fuoco risaltava su "Chiudi"
  const chiudiOra = useRef(onChiudi);
  chiudiOra.current = onChiudi;
  useEffect(() => {
    chiudi.current?.focus();
    const tasto = (e) => { if (e.key === 'Escape') chiudiOra.current(); else trattieniFocus(e, finestra.current); };
    document.addEventListener('keydown', tasto);
    const prima = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', tasto); document.body.style.overflow = prima; };
  }, []);

  const tipo = doc.content_type || stato.blob?.type || '';
  const pdf = tipo === 'application/pdf';
  const ultima = pagine || null;
  return (
    <div className="anteprima-doc" ref={finestra} role="dialog" aria-modal="true" aria-label={t('documenti.anteprima.titolo', { nome: doc.tipo_nome || doc.nome_file })} data-testid="anteprima-documento">
      <div className="testa-a">
        <b>{doc.tipo_nome || t('documenti.anteprima.documento')}<small>{doc.nome_file}</small></b>
        {azioni}
        <button type="button" className="p" disabled={!stato.blob} onClick={() => salvaBlob(stato.blob, doc.nome_file)}>{t('comune.scarica')}</button>
        <button type="button" ref={chiudi} onClick={onChiudi} aria-label={t('documenti.anteprima.chiudi_aria')}>{t('comune.chiudi')}</button>
      </div>
      {/* Le frecce di un PDF di più pagine: prima l'anteprima non ne aveva, e di un contratto di otto pagine si
          vedeva quello che decideva il visore del browser (su iPhone: niente). Compaiono solo quando servono. */}
      {pdf && (!ultima || ultima > 1) && (
        <div className="pagine-a">
          <button type="button" aria-label={t('documenti.anteprima.pagina_prec')} disabled={pagina <= 1}
            onClick={() => setPagina((p) => Math.max(1, p - 1))}>‹</button>
          <small data-testid="anteprima-pagina">
            {ultima ? t('documenti.anteprima.pagina_di', { n: pagina, tot: ultima }) : t('documenti.anteprima.pagina', { n: pagina })}
          </small>
          <button type="button" aria-label={t('documenti.anteprima.pagina_succ')} disabled={Boolean(ultima) && pagina >= ultima}
            onClick={() => setPagina((p) => (ultima ? Math.min(ultima, p + 1) : p + 1))}>›</button>
        </div>
      )}
      <div className="corpo">
        {stato.errore && <p>{stato.errore}</p>}
        {!stato.errore && !stato.url && <p>{t('documenti.anteprima.apro')}</p>}
        {stato.url && tipo.startsWith('image/') && <img src={stato.url} alt={doc.tipo_nome || t('documenti.anteprima.documento')} />}
        {stato.blob && pdf && (
          <VisorePdf blob={stato.blob} url={stato.url} pagina={pagina} onPagine={setPagine}
            titolo={doc.tipo_nome || t('documenti.anteprima.documento')} />
        )}
        {stato.url && !tipo.startsWith('image/') && !pdf && <p>{t('documenti.anteprima.non_si_vede')}</p>}
      </div>
      {piede && <div className="piede">{piede}</div>}
    </div>
  );
};

export default Anteprima;
